import React, { useEffect, useMemo, useState } from "react";
import { CalendarCheck, ShieldCheck, Receipt, FileText, Stethoscope, ChevronDown, MapPin, Search, ArrowUp } from "lucide-react";
const PRACTICE_NAME = "Alpha Dental – Universal City";
const PHONE_NUMBER = "(210) 775-5177";
const PHONE_TEL = "tel:2107755177";
const SITE_URL = "https://ddsalpha.com";
const ADDRESS_LINE1 = "916 Coronado Blvd";
const CITY = "Universal City";
const STATE = "TX";
const ZIP = "78148";
const FULL_ADDRESS = `${ADDRESS_LINE1}, ${CITY}, ${STATE} ${ZIP}`;
const NEARBY_LANDMARK = "Randolph Air Force Base";

// ---------- Types ----------
type TabKey = "appointments" | "insurance" | "billing" | "forms" | "treatments";
type FAQ = {
  q: string;
  a: string;
};
type TabDef = {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  faqs: FAQ[];
  popularSearches: string[];
};

// ---------- JSON-LD ----------
function buildFaqSchema(faq: FAQ[], pageTitle?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(pageTitle ? {
      name: pageTitle
    } : {}),
    mainEntity: faq.map(item => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a
      }
    }))
  };
}
function buildDentistSchema() {
  // Note: Add geo/priceRange/openingHours later if you want.
  return {
    "@context": "https://schema.org",
    "@type": "Dentist",
    name: PRACTICE_NAME,
    url: SITE_URL,
    telephone: PHONE_TEL,
    address: {
      "@type": "PostalAddress",
      streetAddress: ADDRESS_LINE1,
      addressLocality: CITY,
      addressRegion: STATE,
      postalCode: ZIP,
      addressCountry: "US"
    },
    areaServed: [`${CITY}, ${STATE}`, "Live Oak, TX", "Selma, TX", "Schertz, TX", "Converse, TX", "San Antonio, TX"],
    keywords: "dentist Universal City TX, family dentist Universal City, general dentistry, dental implants, All-on-4, All-on-X, emergency dentist, Medicaid dentist, TRICARE dentist, Randolph AFB dentist"
  };
}
function JsonLd({
  data
}: {
  data: any;
}) {
  const json = JSON.stringify(data);
  return <script type="application/ld+json" dangerouslySetInnerHTML={{
    __html: json
  }} />;
}
function FaqJsonLd({
  faq,
  title
}: {
  faq: FAQ[];
  title?: string;
}) {
  if (!faq?.length) return null;
  return <JsonLd data={buildFaqSchema(faq, title)} />;
}

// ---------- Simple Accordion ----------
function FAQAccordion({
  faqs
}: {
  faqs: FAQ[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <div className="rounded-2xl border border-muted bg-background/80 shadow-soft">
      {faqs.map((item, idx) => {
      const open = openIndex === idx;
      return <div key={`${item.q}-${idx}`} className="border-b border-muted last:border-b-0">
            <button type="button" className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left" onClick={() => setOpenIndex(open ? null : idx)} aria-expanded={open}>
              <span className="font-semibold text-foreground">{item.q}</span>
              <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${open ? "rotate-180" : "rotate-0"}`} aria-hidden="true" />
            </button>

            <div className={`${open ? "block" : "hidden"} px-6 pb-5 text-muted-foreground`}>
              <p className="leading-relaxed">{item.a}</p>
            </div>
          </div>;
    })}
    </div>;
}

// ---------- Tab Button ----------
function TabButton({
  active,
  icon,
  label,
  onClick
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition border whitespace-nowrap ${active ? "bg-primary text-foreground-primary border-primary" : "bg-background text-foreground border-muted hover:bg-muted/30"}`} aria-pressed={active}>
      {icon}
      {label}
    </button>;
}

// ---------- Service Areas ----------
function ServiceAreas() {
  const areas = ["Universal City", "Live Oak", "Selma", "Schertz", "Converse"];
  return <div className="mt-8 rounded-2xl border border-muted bg-background/70 p-6 sm:p-8 shadow-soft">
      <div className="flex items-center justify-center gap-2 text-foreground">
        <MapPin className="h-5 w-5 text-primary" />
        <h2 className="text-xl sm:text-2xl font-semibold">
          Local Dentist Near {CITY}, TX — Service Areas
        </h2>
      </div>

      <p className="mt-3 text-center text-muted-foreground max-w-3xl mx-auto">
        {PRACTICE_NAME} is located at <strong>{FULL_ADDRESS}</strong>, near{" "}
        <strong>{NEARBY_LANDMARK}</strong>. We serve patients from the northeast
        San Antonio area and nearby communities.
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {areas.map(a => <span key={a} className="inline-flex items-center rounded-full border border-muted bg-background/80 px-4 py-2 text-sm font-semibold text-foreground">
            {a}, TX
          </span>)}
      </div>

      <p className="mt-4 text-center text-xs text-muted-foreground">
        Need help finding us? Call{" "}
        <a className="underline font-semibold" href={PHONE_TEL}>
          {PHONE_NUMBER}
        </a>
        .
      </p>
    </div>;
}

// ---------- Popular Searches Block ----------
function PopularSearches({
  items
}: {
  items: string[];
}) {
  return <div className="rounded-2xl border border-muted bg-background/70 p-6">
      <div className="flex items-center gap-2">
        <Search className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">
          Popular Local Searches
        </h3>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        These are common searches patients use when looking for a{" "}
        <strong>dentist in Universal City, TX</strong>.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {items.map(k => <span key={k} className="inline-flex rounded-full border border-muted bg-background/80 px-3 py-1 text-xs font-semibold text-foreground">
            {k}
          </span>)}
      </div>
    </div>;
}

// ---------- Helpers ----------
function dedupeFaqs(faqs: FAQ[]) {
  const seen = new Set<string>();
  return faqs.filter(f => {
    const key = f.q.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function pickSchemaFaqs(all: FAQ[], max = 30) {
  // Google typically prefers a smaller, high-quality set for FAQPage schema.
  return dedupeFaqs(all).slice(0, max);
}

// ---------- Page ----------
export default function ContactPage() {
  const [tab, setTab] = useState<TabKey>("appointments");
  const [tabsSticky, setTabsSticky] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setTabsSticky(window.scrollY > 220);
      setShowBackToTop(window.scrollY > 650);
    };
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const scrollToTop = () => window.scrollTo({
    top: 0,
    behavior: "smooth"
  });

  // ---------- 20 FAQs per category ----------
  const appointmentFaqs: FAQ[] = [{
    q: `How do I schedule a dental appointment at ${PRACTICE_NAME}?`,
    a: `Call ${PHONE_NUMBER} to schedule an appointment at our dental office in ${CITY}, ${STATE}. We’ll confirm a time, review your needs, and explain what to bring for your visit.`
  }, {
    q: "Do you accept walk-ins or same-day dental appointments in Universal City, TX?",
    a: `Same-day appointments may be available depending on the schedule. If you have tooth pain, swelling, or a dental emergency, call ${PHONE_NUMBER} as early as possible for urgent care.`
  }, {
    q: "Where is Alpha Dental located in Universal City, TX?",
    a: `Our address is ${FULL_ADDRESS}. We’re near ${NEARBY_LANDMARK} and convenient for patients in Universal City, Live Oak, Converse, and northeast San Antonio.`
  }, {
    q: "What should I bring to my first dental appointment?",
    a: "Bring a photo ID, insurance card (if applicable), a current medication list, and any prior dental records or X-rays you may have. If you don’t have records, we can take digital X-rays as needed."
  }, {
    q: "How long does a new patient dental appointment take?",
    a: "New patient appointments typically take longer than routine visits because we review your health history, take X-rays if needed, complete a comprehensive exam, and discuss a personalized treatment plan."
  }, {
    q: "What happens during a dental exam and cleaning?",
    a: "A routine visit often includes medical history review, gum evaluation, digital X-rays if needed, a comprehensive dental exam, professional teeth cleaning, and recommendations to prevent cavities and gum disease."
  }, {
    q: "Do you offer family dentistry for kids, teens, adults, and seniors?",
    a: `Yes. We provide family and general dentistry for all ages in ${CITY}, ${STATE}, including preventive care, fillings, crowns, and guidance to keep smiles healthy long-term.`
  }, {
    q: "How early should I arrive for my appointment?",
    a: "New patients should arrive 10–15 minutes early to complete check-in steps. Returning patients should arrive a few minutes early to stay on schedule."
  }, {
    q: "What if I’m running late or need to reschedule?",
    a: `Please call ${PHONE_NUMBER} as soon as possible. We’ll do our best to adjust, but we may need to reschedule depending on the day’s schedule.`
  }, {
    q: "Do you provide emergency dental care in Universal City, TX?",
    a: "Yes. We treat dental emergencies such as toothaches, broken teeth, swelling, infections, and dental trauma. Call the office for the fastest guidance and next available time."
  }, {
    q: "Do you treat tooth pain and dental infections?",
    a: "Yes. We evaluate the cause of tooth pain, which may include cavities, cracks, gum infection, or abscess. Treatment may involve fillings, root canal therapy, extraction, or antibiotics when clinically appropriate."
  }, {
    q: "Do you offer digital X-rays and oral cancer screenings?",
    a: "Yes. Digital X-rays help diagnose decay, infection, and bone health. Oral cancer screenings support early detection and are often part of preventive visits."
  }, {
    q: "Can I request a specific dentist for my visit?",
    a: "When scheduling, you can request a preferred dentist. Availability depends on the provider’s schedule and the type of appointment needed."
  }, {
    q: "Do you offer care for patients near Randolph AFB?",
    a: `Yes. Our dental office near ${NEARBY_LANDMARK} welcomes military families and local residents seeking general dentistry, emergency care, and dental implants.`
  }, {
    q: "Do you offer treatment plans and clear explanations?",
    a: "Yes. We explain findings in plain language and review recommended options so you can choose the care that fits your goals, timeline, and budget."
  }, {
    q: "Do you provide teeth whitening or cosmetic dentistry consultations?",
    a: "Yes. Cosmetic dentistry options may include whitening and other treatments to improve tooth color, shape, and overall smile appearance."
  }, {
    q: "Do you provide crowns and bridges?",
    a: "Yes. Dental crowns strengthen damaged teeth, and bridges can replace missing teeth to restore your bite and chewing function."
  }, {
    q: "Do you provide tooth-colored fillings?",
    a: "Yes. Composite (tooth-colored) fillings repair cavities and minor chips while blending naturally with your smile."
  }, {
    q: "Do you provide gum disease treatment (deep cleanings)?",
    a: "Yes. If gum disease is present, we may recommend scaling and root planing (deep cleaning) and periodontal care to reduce inflammation and protect bone and gum health."
  }, {
    q: "Do you offer wisdom teeth evaluations and extractions?",
    a: "Yes. We evaluate wisdom teeth and discuss extraction when indicated, plus replacement options when needed for other extractions."
  }];
  const insuranceFaqs: FAQ[] = [{
    q: "What dental insurance plans do you accept in Universal City, TX?",
    a: "We accept many PPO dental insurance plans and can help verify your benefits. Coverage varies by plan and employer group, so call with your details for confirmation."
  }, {
    q: "Do you accept Medicaid dental insurance?",
    a: "Yes, we accept Medicaid. Covered services and eligibility depend on your plan rules and current coverage."
  }, {
    q: "Do you accept TRICARE dental plans near Randolph AFB?",
    a: `We accept TRICARE dental plans (coverage varies). If you’re near ${NEARBY_LANDMARK}, call us and we’ll help verify benefits and eligibility when available.`
  }, {
    q: "Do you accept Medicare Advantage dental plans?",
    a: "We accept many Medicare Advantage plans with dental benefits. Coverage and limitations vary, so we verify benefits whenever possible before treatment."
  }, {
    q: "Can you verify my dental insurance benefits before my appointment?",
    a: "Yes. We help verify eligibility, deductibles, annual maximums, waiting periods, and covered services when your carrier provides that information."
  }, {
    q: "Will I receive an estimate before treatment begins?",
    a: "Yes. For most planned treatment, we provide an estimate that outlines recommended care, expected insurance coverage, and estimated out-of-pocket cost when possible."
  }, {
    q: "What affects my out-of-pocket dental cost?",
    a: "Your out-of-pocket cost can depend on deductibles, copays/coinsurance, annual maximums, waiting periods, plan limitations, and whether a service is covered as preventive, basic, or major."
  }, {
    q: "Do you file dental insurance claims for patients?",
    a: "In many cases, yes. We can help submit claims and provide documentation needed by your insurer, but coverage decisions ultimately come from the insurance carrier."
  }, {
    q: "What is the difference between in-network and out-of-network dental coverage?",
    a: "In-network plans may have negotiated fees. Out-of-network plans may reimburse differently. We can discuss what this means for your estimate based on your specific plan."
  }, {
    q: "Do you accept Delta Dental?",
    a: "We work with many PPO carriers and can check Delta Dental details for your plan and group. Call us with your member information for verification."
  }, {
    q: "Do you accept Cigna dental insurance?",
    a: "We work with many PPO plans including Cigna options. Call us to confirm your specific plan benefits and coverage."
  }, {
    q: "Do you accept Aetna dental insurance?",
    a: "We work with many PPO plans including Aetna options. Coverage depends on plan type; we can verify details for you."
  }, {
    q: "Do you accept MetLife dental insurance?",
    a: "We work with many PPO plans including MetLife options. We can help verify eligibility and covered services when available."
  }, {
    q: "Do you accept Guardian dental insurance?",
    a: "We work with many PPO plans including Guardian options. Please call so we can check your plan details."
  }, {
    q: "Do you accept Humana dental insurance?",
    a: "We work with many PPO plans including Humana options. Coverage varies, so we verify benefits based on your plan."
  }, {
    q: "Do you accept UnitedHealthcare dental insurance?",
    a: "We work with many PPO plans including UnitedHealthcare dental options. Call us for benefit verification."
  }, {
    q: "Do you accept Blue Cross Blue Shield dental?",
    a: "We work with many BCBS dental options depending on the network and plan. We can verify your coverage details."
  }, {
    q: "Do you accept Ameritas or Principal dental plans?",
    a: "We work with many PPO plans including Ameritas and Principal options. We can verify the plan and estimate coverage."
  }, {
    q: "Do you accept United Concordia or GEHA?",
    a: "We work with many PPO plans including United Concordia and GEHA options. Call our team to confirm coverage details."
  }, {
    q: "Do you explain insurance coverage in plain language?",
    a: "Yes. We walk through what’s covered, what isn’t, and what the estimated patient portion may be so there are fewer surprises."
  }];
  const billingFaqs: FAQ[] = [{
    q: "Do you offer dental financing or monthly payment plans in Universal City, TX?",
    a: "Yes. Financing options may be available to help you pay over time for larger treatments such as crowns, implants, or full-arch implant dentistry. Ask our team for current options."
  }, {
    q: "What financing partners do you work with?",
    a: "We may offer financing through partners such as CareCredit, Sunbit, and Cherry. Availability and terms depend on approval and partner programs."
  }, {
    q: "Can I get a cost estimate for dental implants?",
    a: "Yes. Implant cost depends on how many implants, bone support, needed imaging, and the final restoration. We provide a personalized estimate after an exam and appropriate diagnostics."
  }, {
    q: "How much do crowns cost with insurance?",
    a: "Costs vary by plan coverage and remaining benefits. We provide an estimate showing expected insurance contribution and your portion before proceeding whenever possible."
  }, {
    q: "Do you offer transparent pricing before treatment?",
    a: "Yes. We review the recommended treatment plan and provide clear, upfront estimates for planned services."
  }, {
    q: "What payment methods do you accept?",
    a: "Most dental offices accept major credit cards and other standard payment options. Call the front desk for the exact accepted methods."
  }, {
    q: "Do you offer payment options for patients without insurance?",
    a: "Yes. We can discuss self-pay estimates and available payment/financing options to keep care accessible."
  }, {
    q: "Do you charge for emergency dental visits?",
    a: "Emergency visits typically include an evaluation and any needed imaging. Costs depend on what’s required. We will review estimates when possible."
  }, {
    q: "Does insurance cover dental implants?",
    a: "Some plans may contribute to implants or the restoration, while others may have limitations. We verify benefits when possible and review expected coverage with you."
  }, {
    q: "Does insurance cover root canal therapy?",
    a: "Many plans cover root canals depending on tooth type and plan category (basic/major). We can verify and provide an estimate."
  }, {
    q: "Does insurance cover wisdom teeth extraction?",
    a: "Coverage varies. We can verify benefits and provide an estimate based on the recommended extraction type and your plan."
  }, {
    q: "Can I finance All-on-4 / All-on-X full-arch implants?",
    a: "Financing may be available for full-arch implant treatment. Terms depend on approval and the financing partner’s program."
  }, {
    q: "What is included in an implant consultation?",
    a: "An implant consultation typically includes exam, discussion of goals, review of bone/health factors, and imaging recommendations (often 3D CBCT if needed) to plan safe implant placement."
  }, {
    q: "Do you help patients understand deductibles and annual maximums?",
    a: "Yes. We help explain deductibles, annual maximums, and remaining benefits when the insurance carrier provides that information."
  }, {
    q: "Do you provide itemized receipts for insurance or reimbursement?",
    a: "We can typically provide documentation/receipts and coding details as needed for plan submission or reimbursement requirements."
  }, {
    q: "What if my insurance denies a claim?",
    a: "Denials can happen due to plan limitations or missing documentation. We can help provide requested records when appropriate, but final decisions are made by the carrier."
  }, {
    q: "Do you offer billing help if I have questions?",
    a: `Yes. Call ${PHONE_NUMBER} and our team can review estimates, statements, and insurance communications with you.`
  }, {
    q: "Are treatment costs the same for everyone?",
    a: "No. Costs vary based on your clinical needs, the complexity of treatment, materials used, and your insurance coverage and remaining benefits."
  }, {
    q: "Can I split payments across visits for phased treatment?",
    a: "Many treatment plans can be phased. We can discuss scheduling and payment timing depending on your clinical needs and the treatment sequence."
  }, {
    q: "Do you offer clear timelines and priorities for treatment?",
    a: "Yes. We explain what is urgent (pain/infection), what is important (decay/gum disease), and what is elective (cosmetic) to help you plan confidently."
  }];
  const formsFaqs: FAQ[] = [{
    q: "Do you have new patient registration forms?",
    a: "Yes. Our team can guide you to new patient paperwork and explain what information is required for your first visit."
  }, {
    q: "Can I complete forms before I arrive?",
    a: "In many cases, yes. Ask our team and we’ll provide instructions for completing forms ahead of time when available."
  }, {
    q: "Do you accept transfer patients from another dental office?",
    a: "Yes. If you’re transferring care, we can help request records and X-rays from your previous dentist when needed."
  }, {
    q: "How do I request dental records from my previous dentist?",
    a: "Contact your previous office and request a records release. We can also provide guidance on what information helps most for continuity of care."
  }, {
    q: "Do you keep dental records and X-rays securely?",
    a: "Yes. Patient records are maintained securely and used to support diagnosis, treatment planning, and continuity of care."
  }, {
    q: "Do I need to bring my child’s immunization records for a dental visit?",
    a: "Typically no. For pediatric dental care, what’s most helpful is medical history, medications, allergies, and prior dental records if available."
  }, {
    q: "What medical history information is important for dental treatment?",
    a: "Conditions like diabetes, heart conditions, pregnancy, bleeding disorders, medications, and allergies can affect dental care. Bring a current list of medications and any relevant medical history."
  }, {
    q: "Do you offer paperwork help for Medicaid or TRICARE plans?",
    a: "We can help guide you on what insurance information is needed and help verify coverage when available from the plan."
  }, {
    q: "Do you provide post-op instruction sheets after procedures?",
    a: "Yes. After extractions, root canals, or implant procedures, we provide aftercare guidance and instructions tailored to your procedure."
  }, {
    q: "Can I request a work/school note after my appointment?",
    a: "Yes. If you need documentation for work or school, please ask at the front desk during your visit."
  }, {
    q: "Do you offer consent forms for implants and surgery?",
    a: "Yes. For procedures like dental implants or extractions, we review consent forms, risks, benefits, and alternatives before treatment."
  }, {
    q: "What if I have dental anxiety—can I note it on my forms?",
    a: "Yes. Please tell us about dental anxiety or past experiences so we can adjust communication, pacing, and comfort measures."
  }, {
    q: "Do you offer forms for emergency dental visits?",
    a: "Yes. Emergency visits require basic history and consent forms so we can diagnose and begin care quickly and safely."
  }, {
    q: "Can I update my insurance or contact info online?",
    a: "Often, yes. If not available online, you can update by phone or in-office at check-in."
  }, {
    q: "Do you offer HIPAA privacy practices information?",
    a: "Yes. We provide privacy practices information and can answer questions about how your information is used and protected."
  }, {
    q: "Can a parent/guardian sign forms for a minor patient?",
    a: "Yes. A parent or legal guardian typically signs consent and medical history forms for minors."
  }, {
    q: "Do you provide implant warranty or maintenance guidance?",
    a: "We provide maintenance guidance for implants, including hygiene, home care, and recall schedule recommendations. Any warranty terms depend on clinical findings and materials used."
  }, {
    q: "Do you help with referral paperwork if I need a specialist?",
    a: "If referral is needed, we can help coordinate documentation and share relevant records to support specialty care."
  }, {
    q: "Can I request my own records or X-rays?",
    a: "Yes. Patients can request copies of records subject to standard procedures and identity verification."
  }, {
    q: "What if I don’t have any previous dental records?",
    a: "No problem. We can take new digital X-rays and complete a comprehensive exam to establish a baseline and plan your care."
  }];
  const treatmentsFaqs: FAQ[] = [{
    q: "What dental services do you offer in Universal City, TX?",
    a: "We provide family and general dentistry including exams, cleanings, fillings, crowns, bridges, root canal therapy, gum disease treatment, extractions, emergency dentistry, cosmetic dentistry, dental implants, and All-on-4 / All-on-X full-arch implants."
  }, {
    q: "Do you offer dental implants in Universal City, TX?",
    a: `Yes. We provide dental implant evaluations and treatment planning for missing teeth. For patients near ${NEARBY_LANDMARK}, implants can be a stable long-term tooth replacement option.`
  }, {
    q: "What are All-on-4 / All-on-X full-arch implants?",
    a: "All-on-4 and All-on-X are full-arch implant solutions that replace a full upper or lower set of teeth using strategically placed implants for strong support. Treatment planning is individualized based on bone, bite, and goals."
  }, {
    q: "How long do dental implants last?",
    a: "With proper placement, good home care, and regular professional maintenance, implants can last many years. Longevity depends on health factors, bite forces, and hygiene."
  }, {
    q: "Is implant placement painful?",
    a: "Most patients report manageable discomfort. We use modern techniques to keep care comfortable, and we provide post-op instructions to support healing."
  }, {
    q: "What is the recovery time after dental implant surgery?",
    a: "Recovery varies. Many patients return to normal activities quickly, but full healing and integration can take several months depending on your plan and whether bone grafting is needed."
  }, {
    q: "Do you perform root canal therapy?",
    a: "Yes. Root canal treatment can save an infected or painful tooth by removing infection and sealing the tooth to prevent reinfection."
  }, {
    q: "How do I know if I need a root canal?",
    a: "Symptoms may include lingering tooth pain, sensitivity to hot/cold, swelling, or pain when biting. Diagnosis requires an exam and imaging."
  }, {
    q: "Do you treat gum disease (periodontal disease)?",
    a: "Yes. Gum disease may require deep cleanings (scaling and root planing), ongoing maintenance, and home-care guidance to reduce inflammation and protect bone support."
  }, {
    q: "What should I do after a tooth extraction?",
    a: "Follow post-op instructions: bite on gauze as directed, avoid smoking and straws initially, eat soft foods, keep the area clean, and call us if bleeding or pain worsens."
  }, {
    q: "What should I do if I have swelling or severe tooth pain?",
    a: `Call ${PHONE_NUMBER} right away. Swelling and pain can indicate infection that needs prompt evaluation and treatment.`
  }, {
    q: "Do you offer same-day emergency dentist appointments?",
    a: "Same-day emergency availability depends on the schedule. If you have pain, swelling, or a broken tooth, call as early as possible for triage and next steps."
  }, {
    q: "What are tooth-colored fillings?",
    a: "Tooth-colored (composite) fillings repair cavities and small chips while preserving healthy tooth structure and blending naturally with your teeth."
  }, {
    q: "When do I need a dental crown?",
    a: "Crowns may be recommended after large cavities, fractured teeth, root canal therapy, or when a tooth needs extra strength and protection."
  }, {
    q: "Do you offer cosmetic dentistry in Universal City, TX?",
    a: "Yes. Cosmetic dentistry can improve tooth color, shape, and symmetry. Options vary based on your smile goals and exam findings."
  }, {
    q: "How often should I get a dental cleaning?",
    a: "Many patients benefit from a cleaning every 6 months, but gum health, cavity risk, and medical history may require more frequent visits."
  }, {
    q: "Do you offer oral cancer screenings?",
    a: "Yes. Oral cancer screenings support early detection and are commonly included as part of preventive dental exams."
  }, {
    q: "What foods should I avoid after implant surgery or extraction?",
    a: "Avoid hard, crunchy, spicy, or very hot foods early on. Soft foods and good hydration help support healing. Follow your specific post-op instructions."
  }, {
    q: "How do I care for dental implants long-term?",
    a: "Brush and floss daily, keep regular professional cleanings, and follow recommended maintenance. Implant health depends heavily on hygiene and gum stability."
  }, {
    q: "Do you treat patients from Live Oak, Converse, and northeast San Antonio?",
    a: `Yes. Patients visit us from ${CITY}, Live Oak, Converse, Selma, Schertz, and nearby San Antonio areas for general dentistry, emergency care, and implants.`
  }];

  // ---------- Tabs ----------
  const tabs: TabDef[] = useMemo(() => [{
    key: "appointments",
    label: "Appointments",
    icon: <CalendarCheck className="h-4 w-4" />,
    title: "Appointments & General Dentistry",
    subtitle: `Scheduling, preventive care, and family dentistry at ${FULL_ADDRESS}.`,
    faqs: appointmentFaqs,
    popularSearches: ["dentist Universal City TX", "family dentist Universal City", "teeth cleaning Universal City TX", "emergency dentist Universal City", "dentist near Randolph AFB"]
  }, {
    key: "insurance",
    label: "Insurance",
    icon: <ShieldCheck className="h-4 w-4" />,
    title: "Insurance Accepted & Benefits Help",
    subtitle: "PPO plans, Medicaid, TRICARE, and many Medicare Advantage dental plans (coverage varies).",
    faqs: insuranceFaqs,
    popularSearches: ["Medicaid dentist Universal City", "TRICARE dentist near me", "PPO dental insurance accepted", "Medicare Advantage dental Universal City", "insurance verification dentist"]
  }, {
    key: "billing",
    label: "Billing",
    icon: <Receipt className="h-4 w-4" />,
    title: "Billing, Pricing & Dental Financing",
    subtitle: "Estimates, payment options, and financing for crowns, implants, and full-arch dentistry.",
    faqs: billingFaqs,
    popularSearches: ["dental financing Universal City TX", "payment plans for dental work", "implant financing near me", "All-on-4 cost Universal City", "dental crown cost with insurance"]
  }, {
    key: "forms",
    label: "Forms",
    icon: <FileText className="h-4 w-4" />,
    title: "Forms, Records & New Patient Paperwork",
    subtitle: "New patient registration, record transfers, and post-op instruction guidance.",
    faqs: formsFaqs,
    popularSearches: ["new patient dental forms Universal City", "transfer dental records", "dentist paperwork Universal City TX", "HIPAA dental office forms", "post op instructions tooth extraction"]
  }, {
    key: "treatments",
    label: "Treatments & Post-Op Care",
    icon: <Stethoscope className="h-4 w-4" />,
    title: "Treatments, Dental Implants & Post-Op Care",
    subtitle: "General dentistry, emergency care, dental implants, and All-on-4 / All-on-X full-arch implants.",
    faqs: treatmentsFaqs,
    popularSearches: ["dental implants Universal City TX", "All-on-4 implants near me", "root canal Universal City TX", "gum disease treatment Universal City", "wisdom teeth extraction Universal City"]
  }], []);
  const activeTab = tabs.find(t => t.key === tab) ?? tabs[0];

  // Keep schema to a smaller high-quality subset (FAQ schema best practice)
  const schemaFaqs = useMemo(() => {
    const all = [...appointmentFaqs, ...insuranceFaqs, ...billingFaqs, ...formsFaqs, ...treatmentsFaqs];
    return pickSchemaFaqs(all, 30);
  }, []);
  return <section className="bg-background pb-16 px-4 sm:px-6 lg:px-8">
      {/* ✅ LocalBusiness/Dentist schema */}
      <JsonLd data={buildDentistSchema()} />

      {/* ✅ FAQPage schema (subset) */}
      <FaqJsonLd faq={schemaFaqs} title={`FAQs, Insurance & Treatments — ${PRACTICE_NAME} (${CITY}, ${STATE})`} />

      <div className="mx-auto max-w-6xl">
        {/* Replaced the removed heading with a different SEO-friendly intro (not "Patient Resources & FAQs") */}
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground">
            {PRACTICE_NAME} — Information, Insurance & Treatments
          </h1>
          <p className="mt-3 text-muted-foreground max-w-3xl mx-auto">
            Located at <strong>{FULL_ADDRESS}</strong> near{" "}
            <strong>{NEARBY_LANDMARK}</strong>. For appointments, insurance help,
            or emergency dental care, call{" "}
            <a className="underline font-semibold" href={PHONE_TEL}>
              {PHONE_NUMBER}
            </a>
            .
          </p>
        </div>

        <ServiceAreas />

        <div className={`mt-10 ${tabsSticky ? "sticky top-6 z-40" : ""}`}>
          <div className={`${tabsSticky ? "bg-background/85 backdrop-blur-md border border-muted shadow-soft rounded-2xl px-4 py-3" : ""}`}>
            <div className="flex flex-wrap gap-3 justify-center">
              {tabs.map(t => <TabButton key={t.key} active={t.key === tab} icon={t.icon} label={t.label} onClick={() => setTab(t.key)} />)}
            </div>
          </div>
        </div>

        <div id="faqs" className="mt-8 rounded-2xl border border-muted bg-background/70 p-6 sm:p-8 shadow-soft">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground">
              {activeTab.title}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-3xl mx-auto">
              {activeTab.subtitle}
            </p>
          </div>

          <div className="mb-8">
            <PopularSearches items={activeTab.popularSearches} />
          </div>

          <FAQAccordion faqs={activeTab.faqs} />
        </div>
      </div>

      <button type="button" onClick={scrollToTop} className={`fixed bottom-6 right-6 z-50 rounded-full border border-muted bg-background/90 backdrop-blur px-4 py-3 shadow-soft text-foreground hover:bg-muted/30 transition ${showBackToTop ? "opacity-100" : "opacity-0 pointer-events-none"}`} aria-label="Back to top">
        <span className="inline-flex items-center gap-2 text-sm font-semibold">
          <ArrowUp className="h-4 w-4" />
          Top
        </span>
      </button>
    </section>;
}